import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { SectionTitle, Ornament } from "@/components/SectionTitle";

const CHAPTERS = [
  {
    year: "Before 1498",
    title: "The First Islanders",
    text: "Long before any sail appeared on the horizon, Tobago belonged to its Indigenous peoples: Arawak-speaking communities and, later, the Kalinago. They fished its reefs, farmed its valleys and paddled between the islands in dugout canoes.",
  },
  {
    year: "1498",
    title: "A Glimpse from Columbus",
    text: "Christopher Columbus sighted the island on his third voyage and, so the story goes, called it Bella Forma, the beautiful shape. The name that stuck came later and is believed to come from tobacco, the leaf smoked by its people.",
  },
  {
    year: "1600s – 1700s",
    title: "The Island Nobody Could Keep",
    text: "Then the empires arrived, and none of them could hold on to it. The Dutch, the English and the French all planted flags here. So did the Duchy of Courland, a small Baltic state in today's Latvia. Settlements rose, burned and rose again. Treaties were signed in European palaces by men who had never seen its beaches. All told, Tobago changed hands more than thirty times, a record often said to be unmatched in the Caribbean.",
    highlight: true,
  },
  {
    year: "1700s",
    title: "Pirates, Privateers & Castaways",
    text: "With so many flags coming and going, Tobago's hidden coves became a haven for pirates and privateers. They slipped in for fresh water and timber, then vanished over the horizon. Many believe the island inspired Daniel Defoe's Robinson Crusoe, and a cave near Crown Point still carries Crusoe's name.",
  },
  {
    year: "1776",
    title: "The Rainforest Is Protected",
    text: "In a remarkable act of foresight, the Main Ridge, Tobago's forested spine, was set aside to protect the rains and rivers it gave the island. It is widely regarded as the oldest legally protected rainforest in the Western Hemisphere.",
  },
  {
    year: "1777",
    title: "Forts on the Hills, an Estate by the Bay",
    text: "As rivals circled, the British began building Fort King George above Scarborough and Fort Milford at Crown Point, their cannon trained on the sea. In this same year, amid shifting flags and the scent of the forest, Little Englishman's Bay Estate was established.",
    highlight: true,
  },
  {
    year: "1781 – 1814",
    title: "The Final Tug of War",
    text: "The French seized the island in 1781 and the British took it back in 1793. A treaty returned it to France in 1802, and Britain recaptured it in 1803. Only in 1814 did the flag finally stop changing.",
  },
  {
    year: "1889 – 1962",
    title: "One Nation, Two Islands",
    text: "Tobago was joined with Trinidad in 1889, and in 1962 the two islands became the independent nation of Trinidad and Tobago.",
  },
];

const TREASURES = [
  {
    title: "High-Quality Natural Water",
    text: "The Main Ridge rainforest catches the clouds and feeds countless springs, rivers and waterfalls. Fresh water once drew sailors, and pirates, to these shores, and it remains one of the island's quiet treasures.",
    image: "/images/spring.webp",
    span: "lg:col-span-3",
  },
  {
    title: "Fine-Flavoured Cocoa",
    text: "Trinidad and Tobago is the birthplace of Trinitario cocoa. It is recognised by the International Cocoa Organization as a producer of fine or flavour cocoa, prized by chocolatiers for its notes of fruit and spice.",
    image: "/images/cocoa.webp",
    span: "lg:col-span-3",
  },
  {
    title: "Gorgeous Beaches",
    text: "From the reef-sheltered shallows of Pigeon Point to the wild, forest-fringed curve of Englishman's Bay, often counted among the Caribbean's most beautiful, Tobago's coast is still remarkably unspoilt.",
    image: "/images/hero-bay.webp",
    span: "lg:col-span-2",
  },
  {
    title: "Pirates & Forts",
    text: "Cannon still stand on the ramparts of Fort King George, and the coves where privateers once anchored are as secluded as ever. Every headland has a tale.",
    image: "/images/fort.webp",
    span: "lg:col-span-2",
  },
  {
    title: "Rich Biodiversity",
    text: "More than 200 species of birds, from the noisy cocrico, the national bird, to hummingbirds and motmots. Offshore, coral reefs shelter turtles and manta rays. In 2020 UNESCO named north-east Tobago a Biosphere Reserve.",
    image: "/images/hummingbird.webp",
    span: "lg:col-span-2",
  },
];

const Chapter = ({ c, i }) => (
  <FadeUp delay={0.04 * i} y={20}>
    <li className="relative grid gap-2 border-l border-gold/40 pb-12 pl-8 md:grid-cols-[11rem_1fr] md:gap-8 md:pl-10" data-testid={`history-chapter-${i}`}>
      <span className={`absolute -left-[7px] top-2 h-3.5 w-3.5 rotate-45 border border-gold ${c.highlight ? "bg-gold" : "bg-ivory"}`} />
      <p className="font-caps text-sm tracking-[0.18em] text-gold-deep">{c.year}</p>
      <div>
        <h3 className="font-display text-3xl leading-tight text-heritage">{c.title}</h3>
        <p className="mt-3 max-w-2xl text-lg leading-relaxed text-muted-ink">{c.text}</p>
      </div>
    </li>
  </FadeUp>
);

const Timeline = () => (
  <section id="tobago-history" className="relative scroll-mt-28 overflow-hidden py-20 lg:py-24" data-testid="tobago-history">
    <img src="/images/decor-compass.webp" alt="" aria-hidden="true" className="pointer-events-none absolute -right-20 top-10 hidden w-[360px] opacity-[0.12] lg:block" />
    <div className="relative mx-auto max-w-[1440px] px-5 md:px-10">
      <FadeUp>
        <SectionTitle eyebrow="A History of Tobago" title="The Island Everyone Wanted">
          Few places on earth have been fought over as fiercely as Tobago. Its fresh water, fertile valleys and sheltered bays made it a prize that Europe's empires could never stop chasing.
        </SectionTitle>
      </FadeUp>
      <div className="mx-auto mt-12 grid max-w-4xl grid-cols-3 border-y border-gold/40 text-center">
        {[["30+", "Changes of flag"], ["1776", "Rainforest protected"], ["1777", "Estate established"]].map(([n, l]) => (
          <FadeUp key={l} className="px-2 py-6">
            <p className="font-display text-4xl text-heritage md:text-6xl">{n}</p>
            <p className="mt-1 font-caps text-[11px] tracking-[0.2em] uppercase text-gold-deep">{l}</p>
          </FadeUp>
        ))}
      </div>
      <ol className="mx-auto mt-16 max-w-4xl">
        {CHAPTERS.map((c, i) => <Chapter key={c.year} c={c} i={i} />)}
      </ol>
      <FadeUp className="mt-4 text-center">
        <Link to="/history" data-testid="estate-full-history-link" className="group inline-flex items-center gap-3 border border-gold-deep bg-gold-deep px-7 py-4 font-caps text-[12px] tracking-[0.24em] uppercase text-ivory transition-colors duration-500 hover:border-heritage hover:bg-heritage">
          Explore the Full History <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

const Treasures = () => (
  <section className="bg-ivory-muted py-20 lg:py-24" data-testid="tobago-treasures">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <FadeUp>
        <SectionTitle eyebrow="Treasures of Tobago" title="What Made It Worth Fighting For" />
      </FadeUp>
      <div className="mt-12 grid gap-4 lg:auto-rows-[420px] lg:grid-cols-6">
        {TREASURES.map((t, i) => (
          <FadeUp key={t.title} delay={(i % 3) * 0.08} className={`${t.span} h-[440px] lg:h-full`}>
            <article className="group relative h-full overflow-hidden" data-testid={`treasure-${i}`}>
              <img src={t.image} alt={t.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.06]" />
              <div className="absolute inset-0 bg-gradient-to-t from-heritage-deep via-heritage-deep/60 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <p className="font-caps text-[11px] tracking-[0.3em] text-gold-light">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-2 font-caps text-2xl leading-tight text-ivory">{t.title}</h3>
                <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-ivory/85">{t.text}</p>
              </div>
            </article>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

export const TobagoHistory = () => (
  <>
    <Timeline />
    <Treasures />
  </>
);

export const HistoryTeaser = () => (
  <section className="relative overflow-hidden bg-heritage py-20 text-ivory lg:py-24" data-testid="home-history-teaser">
    <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12">
      <FadeUp className="lg:col-span-6">
        <div className="border border-gold/50 p-3">
          <img src="/images/fort.webp" alt="Old cannon on a colonial fort overlooking a Caribbean bay" loading="lazy" className="aspect-[3/2] w-full object-cover" />
        </div>
      </FadeUp>
      <FadeUp delay={0.1} className="lg:col-span-5 lg:col-start-8">
        <Ornament />
        <p className="mt-3 font-caps text-[12px] tracking-[0.32em] uppercase text-gold">A History of Tobago</p>
        <h2 className="mt-4 font-caps text-3xl leading-tight md:text-[2.7rem]">The Island Everyone Wanted</h2>
        <p className="mt-6 text-lg leading-relaxed text-ivory/85">
          Tobago changed hands more than thirty times as empires fought over its fresh water, fertile valleys and sheltered bays. Pirates, forts, fine cocoa and a rainforest protected since 1776: in 1777, in the middle of it all, Little Englishman's Bay Estate was established.
        </p>
        <div className="mt-8 grid grid-cols-3 border-y border-gold/40 text-center">
          {[["30+", "Changes of flag"], ["1776", "Rainforest protected"], ["1777", "Estate established"]].map(([n, l]) => (
            <div key={l} className="px-1 py-4">
              <p className="font-display text-3xl md:text-4xl">{n}</p>
              <p className="mt-1 font-caps text-[10px] tracking-[0.16em] uppercase text-gold-light">{l}</p>
            </div>
          ))}
        </div>
        <Link to="/history" data-testid="home-history-button" className="group mt-9 inline-flex items-center gap-3 border border-gold bg-gold px-7 py-4 font-caps text-[12px] tracking-[0.24em] uppercase text-heritage-deep transition-colors duration-500 hover:border-ivory hover:bg-ivory">
          Read the History <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export const VisitBand = () => (
  <section className="relative overflow-hidden bg-heritage py-20 text-center lg:py-28" data-testid="estate-visit-band">
    <img src="/images/decor-palms.webp" alt="" aria-hidden="true" className="pointer-events-none absolute -left-10 bottom-0 hidden w-[300px] opacity-[0.12] invert lg:block" />
    <FadeUp className="relative mx-auto max-w-3xl px-5">
      <Ornament className="mx-auto" />
      <h2 className="mt-5 font-caps text-3xl leading-tight text-ivory md:text-5xl">Come and See It for Yourself</h2>
      <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-ivory/85">
        Explore the Nature Park and the River and Waterfalls Trail. Discover a cocoa heritage centuries in the making, and stand on a shore that has watched empires come and go. Few places let you walk through so much history and so much natural beauty in a single day.
      </p>
      <div className="mt-10 flex flex-wrap justify-center gap-4">
        <Link to="/contact" data-testid="visit-enquire-button" className="group inline-flex items-center gap-3 border border-gold bg-gold px-7 py-4 font-caps text-[12px] tracking-[0.24em] uppercase text-heritage-deep transition-colors duration-500 hover:bg-ivory hover:border-ivory">
          Enquire about a visit <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
        </Link>
        <Link to="/nature-exploration" data-testid="visit-explore-button" className="inline-flex items-center gap-3 border border-ivory/50 px-7 py-4 font-caps text-[12px] tracking-[0.24em] uppercase text-ivory transition-colors duration-500 hover:border-gold hover:text-gold">
          Nature & Exploration
        </Link>
      </div>
    </FadeUp>
  </section>
);
